import React, { useEffect, useMemo, useState } from 'react';
import {
  AGENT_COUNTRY_POLICY,
  AgentCountryPolicyConfig,
  AgentCountryPolicyValue,
  getAgentCountryPolicy,
  setAgentCountryPolicy,
  policyLabel,
} from '../services/agentCountryPolicyService';

/**
 * AgentCountryPolicySelector
 *
 * Country admin UI for the per-country **AI-agent presence policy**.
 *
 * Tri-state policy mirrors the WebRTC policy shape so the surface
 * feels uniform inside CountryManagementOverlay:
 *
 *   - Not set    — no decision recorded; agents are allowed by
 *                  default. (Identical to today's behaviour.)
 *   - Allowed    — explicit opt-in. No new behaviour, but lets a
 *                  global admin "lock in" the answer so a future
 *                  country admin can't silently disable the feature.
 *   - Disallowed — country admin denies AI-agent activity. Agent
 *                  position writes for this country fail with
 *                  HTTP 403 + `code: agent_country_disallowed`,
 *                  and the marker layer's listing endpoint returns
 *                  an empty array so existing markers fade out for
 *                  new viewers.
 *
 * IMPORTANT — UX hint: the save button writes to the backend mirror
 * (no on-chain transaction), so Freighter does NOT pop. We surface
 * that explicitly under the form so admins don't think the click
 * silently failed when their wallet doesn't open.
 */

export interface AgentCountryPolicySelectorProps {
  country: number;
  canEdit: boolean;
  walletAddress?: string;
  onChange?: (config: AgentCountryPolicyConfig) => void;
}

const ALL_VALUES: AgentCountryPolicyValue[] = [
  AGENT_COUNTRY_POLICY.NOT_SET,
  AGENT_COUNTRY_POLICY.ALLOWED,
  AGENT_COUNTRY_POLICY.DISALLOWED,
];

const AgentCountryPolicySelector: React.FC<AgentCountryPolicySelectorProps> = ({
  country, canEdit, walletAddress, onChange,
}) => {
  const [config, setConfig] = useState<AgentCountryPolicyConfig | null>(null);
  const [policyValue, setPolicyValue] = useState<AgentCountryPolicyValue>(AGENT_COUNTRY_POLICY.NOT_SET);
  const [reasonInput, setReasonInput] = useState<string>('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let active = true;
    getAgentCountryPolicy(country).then((c) => {
      if (!active) return;
      setConfig(c);
      setPolicyValue(c.policy);
      setReasonInput(c.reason || '');
      setDirty(false);
    }).catch(() => { if (active) setError('load_failed'); });
    return () => { active = false; };
  }, [country]);

  const reasonRequired = policyValue === AGENT_COUNTRY_POLICY.DISALLOWED;
  const reasonError = useMemo(() => {
    if (reasonInput.length > 240) return 'Reason must be <= 240 characters.';
    return null;
  }, [reasonInput]);

  async function save() {
    if (!canEdit) return;
    if (reasonError) { setError(reasonError); return; }
    setBusy(true); setError(null); setSavedAt(null);
    try {
      const next = await setAgentCountryPolicy(country, {
        walletAddress,
        policy: policyValue,
        reason: reasonInput.trim() || null,
        policyVersion: (config?.policyVersion || 0) + 1,
      });
      setConfig(next);
      setPolicyValue(next.policy);
      setReasonInput(next.reason || '');
      setDirty(false);
      setSavedAt(Date.now());
      onChange?.(next);
    } catch (err: any) {
      // The backend already humanises 401/403 with a `details` payload;
      // we surface its message verbatim so the admin sees exactly why
      // the write was refused (wrong country admin, missing wallet,
      // etc.) instead of a generic "save_failed".
      setError(err?.message || 'save_failed');
    } finally {
      setBusy(false);
    }
  }

  function revert() {
    if (!config) return;
    setPolicyValue(config.policy);
    setReasonInput(config.reason || '');
    setDirty(false);
    setError(null);
  }

  return (
    <div style={{ padding: '10px 12px', border: '1px solid #2a3a4a', borderRadius: 8, background: '#0d1721' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>AI-agent presence policy</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, marginBottom: 10 }}>
        Controls whether AI agents may post movement / position updates inside this country.
        <em style={{ marginLeft: 4 }}>Not set</em> behaves like Allowed (default). Pick
        <em style={{ marginLeft: 4 }}>Disallowed</em> to refuse new agent position writes
        (HTTP 403) and hide existing agents from the public marker layer for new viewers.
      </div>

      {/* --- Tri-state policy --- */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ALL_VALUES.map((v) => {
          const active = v === policyValue;
          return (
            <button
              key={v}
              disabled={!canEdit || busy}
              onClick={() => { setPolicyValue(v); setDirty(true); }}
              style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 12,
                cursor: canEdit ? 'pointer' : 'default',
                background: active
                  ? (v === AGENT_COUNTRY_POLICY.DISALLOWED ? '#b91c1c' : '#1d4ed8')
                  : '#111c26',
                color:      active ? '#fff'    : '#cbd5e1',
                border: '1px solid ' + (active
                  ? (v === AGENT_COUNTRY_POLICY.DISALLOWED ? '#b91c1c' : '#1d4ed8')
                  : '#2a3a4a'),
                opacity: canEdit ? 1 : 0.7,
              }}
            >{policyLabel(v)}</button>
          );
        })}
      </div>

      {/* --- Reason (recommended for DISALLOWED) --- */}
      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>
          Reason {reasonRequired
            ? <span style={{ color: '#94a3b8' }}>(recommended — shown to operators whose agents are denied)</span>
            : <span style={{ color: '#64748b' }}>(optional)</span>}
        </label>
        <input
          type="text"
          value={reasonInput}
          maxLength={240}
          disabled={!canEdit || busy}
          onChange={(e) => { setReasonInput(e.target.value); setDirty(true); }}
          placeholder={reasonRequired
            ? 'e.g. "Pending KYC framework for autonomous agents — see ops doc XYZ"'
            : 'Free text, surfaced to denied operators in the API response'}
          style={inputStyle}
        />
      </div>

      {/* --- Current effective state --- */}
      {config && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
          Currently:
          <strong style={{ color: '#cbd5e1', marginLeft: 4 }}>
            {policyLabel(config.policy)}
          </strong>
          {config.policyVersion ? <span> · v{config.policyVersion}</span> : null}
          {config.updatedBy ? (
            <span> · last set by <code style={{ color: '#cbd5e1' }}>{config.updatedBy.slice(0, 6)}…{config.updatedBy.slice(-4)}</code></span>
          ) : null}
        </div>
      )}

      {/* --- Actions --- */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button
            onClick={save}
            disabled={busy || !dirty || !!reasonError}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: (busy || !dirty || !!reasonError) ? '#1e293b' : '#1d4ed8',
              color:      (busy || !dirty || !!reasonError) ? '#64748b' : '#fff',
              border: 'none', cursor: (busy || !dirty || !!reasonError) ? 'not-allowed' : 'pointer',
            }}
            title={reasonError || undefined}
          >{busy ? 'Saving…' : 'Save agent policy'}</button>
          {dirty && (
            <button
              onClick={revert}
              disabled={busy}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12,
                background: '#111c26', color: '#cbd5e1',
                border: '1px solid #2a3a4a', cursor: 'pointer',
              }}
            >Revert</button>
          )}
          {savedAt && !dirty && !error && (
            <span style={{ fontSize: 11, color: '#34d399' }}>Saved.</span>
          )}
        </div>
      )}

      {/* Saving info: explicit because admins watching for Freighter
          will otherwise think the save silently failed. */}
      {canEdit && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          This writes to the backend policy mirror — no on-chain
          transaction is required, so your wallet will <em>not</em>
          open Freighter / the passkey prompt. Authorisation is by
          country/global admin role attached to <code>{walletAddress
            ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`
            : 'the connected wallet'}</code>.
        </div>
      )}

      {(error || reasonError) && (
        <div style={{ color: '#f87171', fontSize: 12, marginTop: 6, wordBreak: 'break-word' }}>
          {error || reasonError}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 7px',
  fontSize: 12,
  backgroundColor: '#111c26',
  border: '1px solid #2a3a4a',
  borderRadius: 4,
  color: '#e2e8f0',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#94a3b8',
  marginBottom: 3,
};

export default AgentCountryPolicySelector;
